import Swal from "sweetalert2";
import { COLORS } from "../../../constants";

export const JUSTIFICATIVA_PAUSA_ALMOCO = "Almoço";
export const JUSTIFICATIVA_PAUSA_JANTA = "Janta";

export type MotivoRapidoPausa =
  | typeof JUSTIFICATIVA_PAUSA_ALMOCO
  | typeof JUSTIFICATIVA_PAUSA_JANTA;

const MAX_JUSTIFICATIVA = 500;

function escapeHtml(value: string): string {
  return value.replace(/[<>&]/g, (ch) =>
    ch === "<" ? "&lt;" : ch === ">" ? "&gt;" : "&amp;"
  );
}

export function montarJustificativaPausa(
  motivoRapido: MotivoRapidoPausa | null,
  texto: string
): { ok: true; justificativa: string } | { ok: false; erro: string } {
  const t = texto.trim();
  let justificativa = t;
  if (motivoRapido) {
    if (!t || t === motivoRapido) {
      justificativa = motivoRapido;
    } else if (
      t === `${motivoRapido}.` ||
      t.startsWith(`${motivoRapido}. `) ||
      t.startsWith(`${motivoRapido} `)
    ) {
      justificativa = t;
    } else {
      justificativa = `${motivoRapido}. ${t}`;
    }
  } else if (t.length < 3) {
    return {
      ok: false,
      erro: "Informe a justificativa (mínimo 3 caracteres) ou marque Almoço/Janta.",
    };
  }

  if (justificativa.length > MAX_JUSTIFICATIVA) {
    return {
      ok: false,
      erro: `A justificativa deve ter no máximo ${MAX_JUSTIFICATIVA} caracteres.`,
    };
  }
  return { ok: true, justificativa };
}

const CARD_OFF =
  "flex:1;display:flex;align-items:center;gap:10px;padding:12px 14px;border:2px solid #e5e5e5;border-radius:10px;cursor:pointer;user-select:none;background:#fff;";
const CARD_ON = `flex:1;display:flex;align-items:center;gap:10px;padding:12px 14px;border:2px solid ${COLORS.PRIMARY};border-radius:10px;cursor:pointer;user-select:none;background:#fff5f5;`;

function motivoDoPopup(popup: HTMLElement): MotivoRapidoPausa | null {
  const almoco = popup.querySelector<HTMLInputElement>("#swal-pausa-almoco");
  const janta = popup.querySelector<HTMLInputElement>("#swal-pausa-janta");
  if (almoco?.checked) return JUSTIFICATIVA_PAUSA_ALMOCO;
  if (janta?.checked) return JUSTIFICATIVA_PAUSA_JANTA;
  return null;
}

export async function pedirJustificativaPausa(
  itemNome?: string,
  textoInicial?: string
): Promise<string | null> {
  const nomeEscapado = itemNome ? escapeHtml(itemNome) : "";
  const result = await Swal.fire({
    title: "Justificativa da pausa",
    html: `${
      nomeEscapado
        ? `<p style="margin:0 0 6px;font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#737373;">Item</p>
           <p style="margin:0 0 12px;font-size:16px;font-weight:700;color:#171717;">${nomeEscapado}</p>`
        : ""
    }
      <p style="margin:0 0 8px;font-size:13px;color:#737373;">Marque o motivo rápido ou descreva abaixo. Cada pausa pede um motivo.</p>
      <div style="display:flex;gap:8px;margin:0 0 12px;">
        <label id="swal-opt-almoco" style="${CARD_OFF}">
          <input type="checkbox" id="swal-pausa-almoco" style="width:18px;height:18px;accent-color:${COLORS.PRIMARY};flex-shrink:0;" />
          <span style="font-weight:700;font-size:15px;color:#171717;">Almoço</span>
        </label>
        <label id="swal-opt-janta" style="${CARD_OFF}">
          <input type="checkbox" id="swal-pausa-janta" style="width:18px;height:18px;accent-color:${COLORS.PRIMARY};flex-shrink:0;" />
          <span style="font-weight:700;font-size:15px;color:#171717;">Janta</span>
        </label>
      </div>`,
    input: "textarea",
    inputValue: textoInicial ?? "",
    inputPlaceholder: "Descreva o motivo desta pausa...",
    inputAttributes: {
      maxlength: String(MAX_JUSTIFICATIVA),
      autocapitalize: "sentences",
    },
    width: "28rem",
    showCancelButton: true,
    confirmButtonText: "Retomar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: COLORS.PRIMARY,
    focusConfirm: false,
    allowEnterKey: true,
    preConfirm: (textoInput: string) => {
      const popup = Swal.getPopup();
      if (!popup) return false;
      const montado = montarJustificativaPausa(
        motivoDoPopup(popup),
        textoInput ?? ""
      );
      if (!montado.ok) {
        Swal.showValidationMessage(montado.erro);
        return false;
      }
      return montado.justificativa;
    },
    didOpen: (popup) => {
      const almoco = popup.querySelector<HTMLInputElement>("#swal-pausa-almoco");
      const janta = popup.querySelector<HTMLInputElement>("#swal-pausa-janta");
      const cardAlmoco = popup.querySelector<HTMLElement>("#swal-opt-almoco");
      const cardJanta = popup.querySelector<HTMLElement>("#swal-opt-janta");
      const textarea = Swal.getInput();
      if (!almoco || !janta || !cardAlmoco || !cardJanta || !textarea) return;

      const pintar = () => {
        cardAlmoco.setAttribute("style", almoco.checked ? CARD_ON : CARD_OFF);
        cardJanta.setAttribute("style", janta.checked ? CARD_ON : CARD_OFF);
      };

      const aplicarMotivo = (motivo: MotivoRapidoPausa | null) => {
        const atual = textarea.value.trim();
        const eraRapido =
          atual === JUSTIFICATIVA_PAUSA_ALMOCO ||
          atual === JUSTIFICATIVA_PAUSA_JANTA ||
          atual === "";
        if (motivo && eraRapido) {
          textarea.value = motivo;
        } else if (!motivo && eraRapido) {
          textarea.value = "";
        }
        pintar();
      };

      almoco.addEventListener("change", () => {
        if (almoco.checked) janta.checked = false;
        aplicarMotivo(motivoDoPopup(popup));
      });
      janta.addEventListener("change", () => {
        if (janta.checked) almoco.checked = false;
        aplicarMotivo(motivoDoPopup(popup));
      });
      pintar();
    },
  });

  if (!result.isConfirmed) return null;
  const value = typeof result.value === "string" ? result.value.trim() : "";
  if (value.length < 3 || value.length > MAX_JUSTIFICATIVA) return null;
  if (/<textarea|swal-pausa/i.test(value)) return null;
  return value;
}
